import { db } from './client';
import type { BezelState, LevelId } from '@/state/types';

// ---------------------------------------------------------------------------
// Module state (not exported — managed via functions below)
// ---------------------------------------------------------------------------

const CLIENT_KEY_STORAGE = 'sc-ak';

function debugEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem('sc-analytics-debug') === '1';
  } catch {
    return false;
  }
}

function dbg(msg: string, data?: unknown): void {
  if (!debugEnabled()) return;
   
  console.info(`[analytics] ${msg}`, data ?? '');
}

function clientKey(): string {
  let k = localStorage.getItem(CLIENT_KEY_STORAGE);
  if (!k) { k = crypto.randomUUID(); localStorage.setItem(CLIENT_KEY_STORAGE, k); }
  return k;
}

let _sessionId: string | null = null;

type AttemptBuffer = {
  id: LevelId;
  startedAtIso: string;
  tokensOnEnter: number;
  notes: Record<string, unknown>;
};

let _attempt: AttemptBuffer | null = null;

function levelCols(id: LevelId): { level_n: number | null; level_special: string | null } {
  if (id.kind === 'LEVEL') return { level_n: id.n, level_special: null };
  return { level_n: null, level_special: id.kind };
}

function resetAttempt(id: LevelId, tokensOnEnter: number): void {
  _attempt = {
    id,
    startedAtIso: new Date().toISOString(),
    tokensOnEnter,
    notes: {},
  };
  dbg('attempt start buffered', { id, tokensOnEnter });
}

function flushAttempt(opts: {
  didSucceed: boolean;
  endReason: 'solved' | 'game_over';
  tokensOnEnd: number;
  endedAtIso?: string;
}): void {
  // Allow buffering even when DB/session isn't ready yet. If we can't write,
  // keep the attempt in memory so a later flush can succeed.
  if (!_attempt) {
    dbg('attempt flush skipped (no buffered attempt)', opts);
    return;
  }
  if (!db) {
    dbg('attempt flush skipped (no db client)', opts);
    return;
  }
  if (!_sessionId) {
    dbg('attempt flush skipped (no session id yet)', opts);
    return;
  }

  const endedAtIso = opts.endedAtIso ?? new Date().toISOString();
  const elapsedMs = Math.max(
    0,
    Date.parse(endedAtIso) - Date.parse(_attempt.startedAtIso)
  );
  const tokensOnEnter = Math.max(0, Math.floor(_attempt.tokensOnEnter));
  const tokensOnEnd = Math.max(0, Math.floor(opts.tokensOnEnd));
  const tokensLost = Math.max(0, tokensOnEnter - tokensOnEnd);

  const row = {
    session_id: _sessionId,
    ...levelCols(_attempt.id),
    started_at: _attempt.startedAtIso,
    ended_at: endedAtIso,
    elapsed_ms: elapsedMs,
    did_succeed: opts.didSucceed,
    end_reason: opts.endReason,
    tokens_on_enter: tokensOnEnter,
    tokens_on_end: tokensOnEnd,
    tokens_lost: tokensLost,
    notes: _attempt.notes,
  };

  dbg('level_attempts insert ->', row);
  void db.from('level_attempts').insert(row).then(({ error }) => {
    if (!error) return;
    if (import.meta.env.DEV) {
       
      console.error('[analytics] level_attempts insert failed', error);
    }
  });

  _attempt = null;
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

type SessionState = {
  cowardStatus: boolean;
  ultimateCoward: boolean;
  spiteMode: boolean;
  flags: { falseEndingShown: boolean };
  bezelState: BezelState;
};

function sessionPayload(s: SessionState) {
  return {
    last_seen_at: new Date().toISOString(),
    coward_status: s.cowardStatus,
    ultimate_coward: s.ultimateCoward,
    spite_mode_unlocked: s.spiteMode,
    false_ending_seen: s.flags.falseEndingShown,
    bezel_dents: s.bezelState.dents,
    bezel_coffee_stain: s.bezelState.coffeeStain,
    bezel_safari_hat: s.bezelState.safariHat,
  };
}

/** Called once on app boot. Upserts the session row and caches the session ID. */
export async function initSession(state: SessionState): Promise<void> {
  if (!db) {
    dbg('initSession skipped (no db client)');
    return;
  }
  dbg('initSession -> upsert sessions');
  const { data, error } = await db
    .from('sessions')
    .upsert({ client_key: clientKey(), ...sessionPayload(state) }, { onConflict: 'client_key' })
    .select('id')
    .single();
  if (error && import.meta.env.DEV) {
     
    console.error('[analytics] initSession failed', error);
  }
  if (!error) {
    _sessionId = (data as { id: string }).id;
    dbg('initSession ok (session id cached)', { sessionId: _sessionId });
  } else {
    dbg('initSession result missing id', { error, data });
  }
}

/** Syncs milestone flags + bezel state after a significant game event. */
export function syncSession(state: SessionState): void {
  if (!db) return;
  if (!_sessionId) {
    dbg('syncSession skipped (no session id yet)');
    return;
  }
  const payload = sessionPayload(state);
  dbg('syncSession ->', payload);
  void db.from('sessions').update(payload).eq('id', _sessionId).then(({ error }) => {
    if (!error) return;
    if (import.meta.env.DEV) {
       
      console.error('[analytics] syncSession failed', error);
    }
  });
}

// ---------------------------------------------------------------------------
// Level attempts
// ---------------------------------------------------------------------------

/** Marks the start of a level attempt (buffered client-side until it ends). */
export function trackEnterLevel(id: LevelId, tokens: number): void {
  resetAttempt(id, tokens);
}

/** Shallow-merges a note patch into the current attempt buffer. */
export function trackAttemptNote(patch: Record<string, unknown>): void {
  if (!_attempt) return;
  _attempt.notes = { ..._attempt.notes, ...patch };
  dbg('attempt note patched', patch);
}

/** Inserts one ended attempt row for a solved level. */
export function trackSolvedAttempt(tokensOnEnd: number, endedAtIso?: string): void {
  dbg('attempt end (solved)', { tokensOnEnd, endedAtIso });
  flushAttempt({
    didSucceed: true,
    endReason: 'solved',
    tokensOnEnd,
    ...(endedAtIso ? { endedAtIso } : {}),
  });
}

/** Inserts one ended attempt row for a game-over transition. */
export function trackGameOverAttempt(tokensOnEnd: number, endedAtIso?: string): void {
  dbg('attempt end (game_over)', { tokensOnEnd, endedAtIso });
  flushAttempt({
    didSucceed: false,
    endReason: 'game_over',
    tokensOnEnd,
    ...(endedAtIso ? { endedAtIso } : {}),
  });
}

/** Inserts a compliment into the compliments table. */
export function trackCompliment(id: LevelId, text: string): void {
  if (!db || !_sessionId) return;
  void db.from('compliments').insert({
    session_id: _sessionId,
    submitted_at: new Date().toISOString(),
    level_n: id.kind === 'LEVEL' ? id.n : null,
    text,
  }).then(({ error }) => {
    if (!error) return;
    if (import.meta.env.DEV) {
       
      console.error('[analytics] compliments insert failed', error);
    }
  });
}
